import type { ColumnState, FilterModel, GridState } from 'ag-grid-community';
import type vizInfos from 'helpers/vizInfos';
import type { AccessResourceTypeT } from 'modules/budget/config/types';
import type { DashboardEventT } from 'modules/decision/components/DashboardPanelDetails/types';
import type { FilterGroupT } from 'modules/decision/types/explore';
import type { PaletteName } from 'styles/theme';

import type {
    ACCESS_BUDGETS_GESTION,
    ACCESS_CADRAGE_GESTION,
    ACCESS_DAF,
    ACCESS_PHASES_GESTION,
    ADMIN,
    ADMIN_BUDGET,
    ADMIN_CATEGORIES,
    AI_ASSISTANT_USER,
    BUDGET_USER,
    DECISION_USER,
    EXPLORE_USER,
    HUMAN_RESOURCES_USER,
    PROJECTIONS_USER,
    RAWDATA,
    SCOPE_PERMISSION_READ,
    SCOPE_PERMISSION_WRITE,
    SUPER_FINANCES_WRITER,
    SUPPORT,
    WIDGET_TYPE,
} from './constants';

export const DataTypes = Object.freeze({
    date: 'date',
    datetime: 'datetime',
    integer: 'integer',
    float: 'float',
    fraction: 'fraction',
    money: 'money',
    category: 'category',
    enum: 'enum',
    text: 'text',
    time: 'time',
    coordinates: 'coordinates',
    polygon: 'polygon',
    tree: 'tree',
    table: 'table',
    year: 'year',
});
/* An enumeration of the data types which could be cumulated*/
export const canCumulateDataTypes: DataTypesT[] = [
    DataTypes.date,
    DataTypes.datetime,
    DataTypes.float,
    DataTypes.integer,
    DataTypes.year,
];

/* An enumeration of the different FilterOperations. */
export const FilterOperations = Object.freeze({
    greater: 'greater',
    greater_or_equal: 'greater_or_equal',
    lower: 'lower',
    lower_or_equal: 'lower_or_equal',
    equal: 'equal',
    not_equal: 'not_equal',
    in_: 'in_',
    not_in: 'not_in',
    contains: 'contains',
    not_contains: 'not_contains',
    is_null: 'is_null',
    is_not_null: 'is_not_null',
    between: 'between',
    extract: 'extract',
});

/* An enumeration of the different FilterOperations. */
export const DynamicFilterOperations = Object.freeze({
    last: 'last',
    current: 'current',
    next_: 'next_',
});

export const dateSpecialValues = [
    'current_year',
    'last_year',
    'current_month',
    'last_month',
    'current_week',
    'last_week',
];

/* An enumeration of the different scales. */
export const TimeScales = Object.freeze({
    day: 'day',
    week: 'week',
    month: 'month',
    quarter: 'quarter',
    semester: 'semester',
    year: 'year',
    schoolYear: 'school_year',
    dow: 'dow',
});

export const SlidingTimeScales = Object.freeze({
    slidingYear: 'sliding_year',
    slidingWeek: 'sliding_week',
});

export const DimensionAggregationStrategies = Object.freeze({
    tree: 'tree',
    flat: 'flat',
});

const FO = FilterOperations;
export const operations: Record<
    DataTypesT,
    (FilterOperationsT | DynamicFilterOperationsT)[]
> = {
    polygon: [],
    table: [],
    coordinates: [],
    date: [
        FO.greater,
        FO.greater_or_equal,
        FO.equal,
        FO.lower,
        FO.lower_or_equal,
        FO.is_null,
        FO.is_not_null,
        FO.between,
    ],
    datetime: [
        FO.greater,
        FO.greater_or_equal,
        FO.equal,
        FO.lower,
        FO.lower_or_equal,
        FO.is_null,
        FO.is_not_null,
        FO.between,
    ],
    integer: [
        FO.greater,
        FO.greater_or_equal,
        FO.lower,
        FO.lower_or_equal,
        FO.equal,
        FO.not_equal,
        FO.is_null,
    ],
    float: [
        FO.greater,
        FO.greater_or_equal,
        FO.lower,
        FO.lower_or_equal,
        FO.equal,
        FO.not_equal,
        FO.is_null,
    ],
    fraction: [
        FO.greater,
        FO.greater_or_equal,
        FO.lower,
        FO.lower_or_equal,
        FO.equal,
        FO.not_equal,
        FO.is_null,
    ],
    money: [
        FO.greater,
        FO.greater_or_equal,
        FO.lower,
        FO.lower_or_equal,
        FO.equal,
        FO.not_equal,
        FO.is_null,
    ],
    category: [
        FO.in_,
        FO.not_in,
        FO.is_null,
        FO.is_not_null,
        FO.contains,
        FO.not_contains,
    ],
    text: [FO.contains, FO.not_contains, FO.is_null, FO.is_not_null],
    time: [FO.greater, FO.lower, FO.equal, FO.not_equal, FO.is_null],
    tree: [
        FO.in_,
        FO.not_in,
        FO.is_null,
        FO.is_not_null,
        FO.contains,
        FO.not_contains,
    ],
    year: [
        FO.greater_or_equal,
        FO.equal,
        FO.not_equal,
        FO.lower_or_equal,
        FO.is_null,
    ],
    enum: [FO.in_, FO.not_in, FO.is_null, FO.is_not_null],
};

export type ScopeBlockT = {
    name: string;
    values: string[];
};

export const AccessTypeValues = [
    'full_access',
    'restricted',
    'no_access',
] as const;
export type AccessTypeT = (typeof AccessTypeValues)[number];

export type GenericAccessTypeT = AccessTypeT | AccessResourceTypeT;

export type UserScopeT = {
    name: string;
    accessType: AccessTypeT;
    blocks: ScopeBlockT[];
};

export type UserScopesT = UserScopeT[];
// WARNING : When adding a role here, the role should be added to HubSpot otherwise the roles sent by the backend to hubspot API will not be updated
export type UserRole =
    | typeof ADMIN
    | typeof RAWDATA
    | typeof ACCESS_DAF
    | typeof ADMIN_CATEGORIES
    | typeof SUPER_FINANCES_WRITER
    | typeof ACCESS_CADRAGE_GESTION
    | typeof ACCESS_BUDGETS_GESTION
    | typeof ACCESS_PHASES_GESTION
    | typeof SUPPORT
    | typeof DECISION_USER
    | typeof EXPLORE_USER
    | typeof AI_ASSISTANT_USER
    | typeof BUDGET_USER
    | typeof PROJECTIONS_USER
    | typeof HUMAN_RESOURCES_USER;

export type UserRoleHeader = typeof ADMIN_BUDGET;

export type RightType = 'all' | 'read' | 'write';

export type AccessibleValues = {
    resource: string;
    values: string[];
};

export type PermissionType =
    | typeof SCOPE_PERMISSION_READ
    | typeof SCOPE_PERMISSION_WRITE;

export type GenderType = 'F' | 'M';
export type ConnectionStatus = 'active' | 'invited' | 'inactive';

export type UserInfoT = {
    email: string;
    firstname: string;
    lastname: string;
    gender?: GenderType;
    job: string;
    displayName?: string;
};

export type UserT = {
    id: number;
    authMethods: string[];
    email: string;
    gestionnaireNodes: string[];
    homeDashboardSlug: string;
    isActive: boolean;
    connectionStatus: ConnectionStatus;
    isAdmin: boolean;
    isScopeGestionnaireUnrestricted: boolean;
    isScopeOrgChartUnrestricted: boolean;
    orgChartNodes: string[];
    rights: string[];
    roles: UserRole[];
    scopes: UserScopesT;
    writeScopes: UserScopesT;
    tutorialSteps: { [key: string]: boolean };
} & UserInfoT;

export type UserInfoWithIdT = UserInfoT & { id: number; isActive: boolean };

export type UserGroupSummaryT = {
    id: number;
    name: string;
    nbMembers: number;
    nbSharedDashboards: number;
    userEmails: string[];
};

export type DashboardCoreInfoT = Pick<DashboardT, 'slug' | 'title'>;

type UserIdentityT = Pick<UserInfoT, 'email' | 'firstname' | 'lastname'>;
export type UserGroupT = UserGroupSummaryT & {
    users: UserIdentityT[];
    sharedDashboards: DashboardCoreInfoT[];
};

export type BlockT = { block: string; operation: string };

export type ExpressionT = {
    block?: string;
    operation?: string;
    any?: BlockT[];
    all?: BlockT[];
};

export type ScopeT = {
    name: string;
    label: string;
    expression: ExpressionT;
    accessType?: AccessTypeT;
};

export type ProtectionT = {
    scope: string;
    blocks: string[];
};

export type YamlProtectionT = {
    scope: string;
    blocks?: string[];
    functions?: string[];
};

export type ScopeOptionsT = {
    value: string;
    label: string;
    section: string;
    values: string[];
};

export type ColumnWidthT = {
    columnName: string;
    width: number;
};
export type ColumnSortingT = {
    columnName: string;
    direction: 'asc' | 'desc';
};

export type VizType = keyof typeof vizInfos;

type AreaT = 'data' | 'column' | 'row' | null;

export type BasePivotFieldT = {
    dataField: string;
    area: AreaT;
    areaIndex?: number;
    summaryType?: string;
};

type PivotFieldT = {
    caption: string;
    dataField?: string;
    dataType: string;
    width: number;
    area?: AreaT;
    isMeasure: boolean;
    summaryType?: string;
    customizeText?: string;
    areaIndex?: number;
    expanded?: boolean;
    format?: FlowAnyObject;
    calculateSummaryValue?: (e: FlowAnyObject) => number;
    calculateCustomSummary?: (options: FlowAnyObject) => void;
};

export type OrderedColumnsT = {
    x?: ResponseColumnT;
    y: ResponseColumnT;
    z?: ResponseColumnT;
};

// Type used into pivotGridSettings. It's useful to persist TCD expanded rows
export type ExpandedRowT = {
    value: string | null;
    children: ExpandedRowT[];
};

// ag-grid state saved in settings, with the ids of the grid columns
export type GridStateSettingsT = Partial<GridState> & { colIds: string[] };

export type PivotGridSettingsT = {
    colDefs: ColumnState[];
    expandedRows: ExpandedRowT[];
};

type DimensionKey = string;
type ColorHexa = string;
export type ColorMapping = { value: string; color: ColorHexa };

export type CustomPalette = Partial<
    Record<PaletteName, Record<DimensionKey, ColorMapping[]>>
>;

export type SortDimensionT = {
    dimension: string;
    order: 'asc' | 'desc';
};

export type Threshold = {
    id?: number;
    goalValue?: number;
    goalMetric?: string;
};

export type CustomizableChartSettings = {
    pageSize?: number;
    showGroupAggregates?: boolean;
    showAggregates?: boolean;
    showFilters?: boolean;
    minLimit?: Threshold;
    maxLimit?: Threshold;
    thresholds?: Threshold[];
    numberText?: string;
    paragraphPrefix?: string;
    paragraphSuffix?: string;
    rowHeight?: 'sm' | 'md' | 'lg';
    pivotField?: string;
    decimalPrecision?: 0 | 1 | 2;
    displayBy?: 'default' | 'million' | 'thousand';
    showLegend?: boolean;
    showLegendOnRightSide?: boolean;
    areTicksRotated?: boolean;
    isStackedBars?: boolean;
    isYearlyComparison?: boolean;
    isNormalized?: boolean;
    areLabelsShown?: boolean;
    areLabelsShownInside?: boolean;
    areLabelsShownOutside?: boolean;
    isTotalShown?: boolean;
    areLabelsVertical?: boolean;
    isEvolutionDisplayed?: boolean;
    xAxisTitle?: string;
    yAxisTitle?: string;
    palette?: PaletteName;
    customPalette?: CustomPalette;
    eChartOptions?: string;
    reverseGroupBy?: boolean;
    pivotTableExportedFromDecisionToBudget?: boolean;
    secondaryYAxis?: string;
    wrapVerticalAxisLabels?: boolean;
    verticalAxisWidth?: 'sm' | 'md' | 'lg';
    axisLabelFontSize?: 'sm' | 'md' | 'lg' | 'default';
    sortDimension?: SortDimensionT;
    useGradientColor?: boolean;
    reverseGradientColor?: boolean;
    xAxisMaxNbValues?: number;
    xAxisNbValuesSlice?: 'first' | 'last';
    yAxisMinValue?: number;
    yAxisMaxValue?: number;
    secondaryYAxisMinValue?: number;
    secondaryYAxisMaxValue?: number;
};

export type ChartSettingsT = {
    columnsWidth?: ColumnWidthT[];
    columnsSorting?: ColumnSortingT[];
    pivotFields?: PivotFieldT[] | BasePivotFieldT[];
    pivotFieldsAggrid?: PivotGridSettingsT;
    argumentAnalyseChartSettings?: GridStateSettingsT;
    gridFilters?: FilterModel;
    columnsHidden?: string[];
    hideToolbar?: boolean;
    columnOrder?: string[];
    useSmartMultiMetrics?: boolean;
    isMedia?: boolean;
    metricsCumulated?: string[];
    numberTitle?: string;
    mapLatCenter?: number;
    mapLonCenter?: number;
    mapZoom?: number;
    mediaUrl?: string;
    legendPosition?: FlowAnyObject;
    hiddenTreeFields?: string[];
    chartConception?: {
        id: string;
        chartDimensionKey: 'x' | 'y' | 'z';
    }[];
} & CustomizableChartSettings;

export type RawDataConfigContentT = {
    [model: string]: { [field: string]: boolean };
};
export type RawDataConfigT = {
    id: number;
    chartKey: number;
    content: RawDataConfigContentT;
};

export type WidgetTypeT = keyof typeof WIDGET_TYPE;

export type PageSeparator = {
    yPos: number; // in pixels
    isCuttingWidget?: boolean;
};
export type ArgumentIndicatorNameT =
    | 'projects_with_budget_inscriptions'
    | 'budget_analysis';

export type DashboardChartT = {
    aggregation: string;
    metrics: SentenceMetricT[];
    dimensions: SentenceDimensionT[];
    filterExpression: ChartFilterExpressionGroupT | null;
    settings: ChartSettingsT;
    vizType: VizType;
    key: number;
    title: string;
    position: {
        [key: string]: { x: number; y: number; w: number; h: number };
    };
    disableDashboardFilters: boolean;
    alertTriggered: boolean;
    columns?: string[];
    customData?: {
        indicatorName: ArgumentIndicatorNameT;
    };
};

export type UserSharingT = {
    key: string | number;
    email: string;
    editAccess: boolean;
};

export type GroupSharingT = {
    key: string | number;
    groupId: number;
    groupName: string;
    nbMembers: number;
    editAccess: boolean;
    userEmails: string[];
};

export type OrgSharingT = {
    isPublic: boolean;
    isRestricted: boolean;
    isRestrictedEditAccess: boolean;
};

type SharingT = {
    usersSharing: UserSharingT[];
    groupsSharing: GroupSharingT[];
    orgSharing: OrgSharingT;
    readUserAccessCount: number;
    writeUserAccessCount: number;
    isSupportAccess?: boolean;
};

export type GroupSharingPayloadT = { id: number; editAccess: boolean };
export type SharingPayloadT = {
    usersSharing: UserSharingT[];
    groupsSharing: GroupSharingPayloadT[];
    orgSharing: OrgSharingT;
    shouldSendEmail: boolean;
    shouldSendNotification: boolean;
};

export type DashboardOwnerT = {
    id: number;
    email: string;
    displayName?: string;
};

type DashboardUserHistoryT = {
    creator: string;
    creationDate?: Date;
    lastEvent?: Pick<DashboardEventT, 'date' | 'userDisplayName'>;
};

export type BaseFolderT = {
    id: number;
    name: string;
};

export type DashboardFolderT = BaseFolderT & {
    parentFolder: BaseFolderT | null;
};

export type DashboardFolderTreeNodeT = DashboardFolderT & {
    dashboards: DashboardT[];
    folders: DashboardFolderTreeNodeT[];
};

export type FolderTreeT = {
    dashboards: DashboardT[];
    folders: DashboardFolderTreeNodeT[];
};

export type IndicatorBaseT = {
    dimensions: ExploreDimensionT[];
    filterTree?: FilterGroupT<SentenceFilterT>;
    metrics: ExploreMetricT[];
    settings: ChartSettingsT;
    title: string;
    vizType: VizType;
};

export type DashboardT = {
    key: number;
    layoutId?: number;
    slug: string;
    canEdit: boolean;
    ownerId?: number;
    title: string;
    tags: string[];
    publicToken?: string;
    isArchived: boolean;
    isRecommended: boolean;
    isUserFavourite: boolean;
    isTemplate: boolean;
    isTutorial: boolean;
    owner: DashboardOwnerT;
    folder: BaseFolderT | null;
    originDashboard: string | null;
    lastViewByUser?: string;
    isInPresentationMode: boolean;
    isFullWidth: boolean;
    userHistory: DashboardUserHistoryT;
} & SharingT;

export type UpdateDashboardPayloadT = {
    title: string;
    isArchived: boolean;
    folder: { id: number } | null;
    isInPresentationMode: boolean;
};

type LayoutItemT = {
    x: number;
    y: number;
    h: number;
    w: number;
};

type Breakstyle = 'xs' | 'md';
type BreakstyleLayout = Record<Breakstyle, LayoutItemT>;

type WidgetContent = {
    text?: string;
    tiptap?: string;
    url?: string;
};

export type WidgetT = {
    position: BreakstyleLayout;
    type: WidgetTypeT;
    key: number;
    content?: WidgetContent;
    chart?: DashboardChartT;
    dashboardKey?: number;
    layoutId: number;
};

export type LayoutT = {
    id: number;
    widgets: WidgetT[];
};

export type ArchivedDashboardT = {
    key: number;
    title: string;
    slug: string;
    owner: DashboardOwnerT;
    userHistory: DashboardUserHistoryT;
};

export type ExportFileT = 'csv' | 'xlsx' | 'image';

export const DASHBOARD_SCOPE = {
    RECENT: 'recent',
    FAVORITE: 'favorite',
    PRIVATE: 'private',
    PUBLIC: 'public',
    RESTRICTED: 'restricted',
    SHARED: 'shared',
    TEMPLATE: 'template',
    TUTORIAL: 'tutorial',
} as const;
export type DashboardScopeT =
    (typeof DASHBOARD_SCOPE)[keyof typeof DASHBOARD_SCOPE];

// SelectionBaseT corresponds to a basic selection type
export type SelectionBaseT = {
    value: string;
    label: string;
    key?: string;
    isBadValue: boolean;
};

export type GenericSelectionBase<T> = {
    value: T;
    label: string;
    isBadValue: boolean;
};

// SelectionTreeT is selected element type in a tree.
// isSubtreeSelected indicates whether the selected element is selected with its children
export type SelectionTreeT = SelectionBaseT & {
    isSubtreeSelected: boolean;
};
export const TEAM_MANTY_EMAILS = ['team@mantic.fr', 'team@manty.eu'];

type DataGetDataPayloadT = {
    columns: RawResponseColumnT[];
    data: ResponseDataT[];
};

export type GetDataPayloadT = {
    data: DataGetDataPayloadT;
};
